import { useLocation, Outlet } from 'react-router-dom';
import { lazy, Suspense, useState } from 'react';
import Sidebar from './Sidebar';
import AppHeader from './AppHeader';
import Fab from './Fab';
import Modal from '@/shared/components/Modal';
import ToastContainer from '@/shared/components/ToastContainer';
import ErrorBoundary from '@/shared/components/ErrorBoundary';
import { TransactionForm } from '@/features/transactions';
import SplitTransactionModal from '@/features/transactions/components/SplitTransactionModal';
import { BillForm } from '@/features/bills';

// ImportModal pulls pdfjs-dist + SheetJS (~2MB raw). Lazy-load so the main
// bundle stays lean for users who never open the statement importer.
const ImportModal = lazy(() => import('@/features/import/components/ImportModal'));
import DashboardSkeleton from '@/features/dashboard/components/DashboardSkeleton';
import TransactionsSkeleton from '@/features/transactions/components/TransactionsSkeleton';
import BudgetsSkeleton from '@/features/budgets/components/BudgetsSkeleton';
import { useIsMobile } from '@/shared/hooks/useMediaQuery';
import { useAuthContext } from '@/app/providers/AuthProvider';
import { useHouseholdContext } from '@/app/providers/HouseholdProvider';
import { useHouseholdData } from '@/app/providers/HouseholdDataProvider';
import { useSettingsContext } from '@/app/providers/SettingsProvider';
import { useCategoriesContext } from '@/app/providers/CategoriesProvider';
import { useToastState } from '@/app/providers/ToastProvider';
import { useModals } from '@/app/providers/ModalsProvider';
import { useDashboardData } from '@/features/dashboard';
import { usePeriod } from '@/app/providers/PeriodProvider';
import { DEFAULT_DATA } from '@/shared/constants/config';

export default function AppLayout() {
  const mobile = useIsMobile();
  const location = useLocation();
  const [sideOpen, setSideOpen] = useState(false);

  const { user, handleLogout: authLogout } = useAuthContext();
  const { householdName, inviteCode, members, handleLeaveHousehold, resetHousehold } = useHouseholdContext();
  const { data, loading, clearData, householdId } = useHouseholdData();
  const settingsCtx = useSettingsContext();
  const { isDark, t, getDisplayName, currency, settings } = settingsCtx;
  const { incomeList, expenseMap, allSubCats, getGroup } = useCategoriesContext();
  const { toasts, removeToast } = useToastState();
  const { tx, bills: billsHook, mappings, importOpen, setImportOpen, fabOpen, setFabOpen } = useModals();
  const { selMonth, selYear } = usePeriod();

  const safe = data || DEFAULT_DATA;
  const txns = safe.txns || [];
  const budgets = safe.budgets || {};
  const resolvedUserName = user ? getDisplayName(user.email) : 'User';
  const resolvedMemberNames = members.length > 0 ? members.map(getDisplayName) : [resolvedUserName];

  const dashboard = useDashboardData({
    txns, incomeList, expenseMap, getGroup,
    currentBudgets: budgets[selYear + '-' + (selMonth + 1)] || {},
    selMonth, selYear,
  });
  const { monthNet } = dashboard;

  const handleLogout = async () => {
    await authLogout();
    clearData();
    resetHousehold();
  };

  const isLoading = loading || !data;
  const currentTabId = location.pathname.replace(/^\/+/, '').split('/')[0] || 'dashboard';

  return (
    <div style={{ display: 'flex', height: '100vh', background: t.bg, fontFamily: 'system-ui,-apple-system,sans-serif', color: t.text, transition: 'background 0.3s, color 0.3s' }}>
      <style>{`
        .sidebar-nav { transform: translateX(0); }
        .hamburger-btn { display: none !important; }
        @media (max-width: 768px) {
          .sidebar-nav { position: fixed !important; transform: translateX(-100%); }
          .sidebar-nav.open { transform: translateX(0); }
          .hamburger-btn { display: block !important; }
        }
        .themed-card { background: ${t.cardBg}; border-color: ${t.cardBorder}; transition: background 0.3s; }
        .themed-input { background: ${t.inputBg} !important; color: ${t.text} !important; border-color: ${t.inputBorder} !important; }
        .themed-select { background: ${t.inputBg} !important; color: ${t.text} !important; border-color: ${t.inputBorder} !important; }
      `}</style>
      {sideOpen && <div className="hamburger-btn" onClick={() => setSideOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 30, backdropFilter: 'blur(2px)' }} />}

      <Sidebar
        sideOpen={sideOpen} setSideOpen={setSideOpen}
        householdName={householdName} inviteCode={inviteCode}
        resolvedMemberNames={resolvedMemberNames} resolvedUserName={resolvedUserName}
        monthNet={monthNet} onLogout={handleLogout}
      />

      <main style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
        <AppHeader setSideOpen={setSideOpen} sideOpen={sideOpen} />

        <div style={{ padding: mobile ? 16 : '24px 28px', maxWidth: 1140, margin: '0 auto' }}>
          {isLoading ? (
            currentTabId === 'transactions' ? <TransactionsSkeleton theme={t} /> :
            currentTabId === 'budget' ? <BudgetsSkeleton theme={t} /> :
            <DashboardSkeleton theme={t} />
          ) : (
            <Outlet context={{ handleLeaveHousehold, onLogout: handleLogout, resolvedMemberNames, resolvedUserName, dashboard }} />
          )}
        </div>
      </main>

      {!isLoading && (
        <Fab
          fabOpen={fabOpen}
          setFabOpen={setFabOpen}
          onAddTx={tx.openAddTx}
          onImport={() => setImportOpen(true)}
          theme={t}
          mobile={mobile}
        />
      )}

      {tx.modal && (
        <Modal
          onClose={() => tx.setModal(null)}
          title={tx.modal === 'tx'
            ? (tx.editId ? 'Edit Transaction' : 'Add Transaction')
            : (billsHook.editBillId ? 'Edit Bill' : 'Add Recurring Bill')}
          cardBg={t.cardBg} cardBorder={t.cardBorder} textColor={t.text}
        >
          <ErrorBoundary name="the form">
            {tx.modal === 'tx' && (
              <TransactionForm
                txForm={tx.txForm} setTxForm={tx.setTxForm}
                txErrors={tx.txErrors} setTxErrors={tx.setTxErrors}
                editId={tx.editId} saveTx={tx.saveTx}
                onClose={() => tx.setModal(null)}
                incomeList={incomeList} allSubCats={allSubCats} getGroup={getGroup}
                resolvedMemberNames={resolvedMemberNames}
                theme={t} isDark={isDark}
              />
            )}
            {tx.modal === 'bill' && (
              <BillForm
                billForm={billsHook.billForm} setBillForm={billsHook.setBillForm}
                errors={billsHook.billErrors} setErrors={billsHook.setBillErrors}
                saveBill={billsHook.saveBill} onClose={() => tx.setModal(null)}
                editing={!!billsHook.editBillId}
              />
            )}
          </ErrorBoundary>
        </Modal>
      )}

      {tx.splitTarget && (
        <SplitTransactionModal
          transaction={tx.splitTarget}
          memberNames={resolvedMemberNames}
          onSplit={tx.splitTx}
          onClose={tx.closeSplitTx}
          theme={t}
          isDark={isDark}
        />
      )}

      {importOpen && (
        <ErrorBoundary
          name="import"
          fallback={(err, reset) => (
            <Modal onClose={() => { reset(); setImportOpen(false); }} title="Import error" cardBg={t.cardBg} cardBorder={t.cardBorder} textColor={t.text}>
              <div style={{ padding: 8 }}>
                <p style={{ color: t.errorText, fontSize: 13 }}>Something went wrong. Close and try again.</p>
                {import.meta.env.DEV && <pre style={{ fontSize: 11, background: '#fff', padding: 8, borderRadius: 6 }}>{String(err.message)}</pre>}
              </div>
            </Modal>
          )}
        >
          <Suspense
            fallback={
              <Modal onClose={() => setImportOpen(false)} title="Loading importer..." cardBg={t.cardBg} cardBorder={t.cardBorder} textColor={t.text}>
                <div style={{ padding: 16, fontSize: 13, color: t.textMuted }}>Fetching statement parser...</div>
              </Modal>
            }
          >
            <ImportModal
              householdId={householdId} txns={txns}
              incomeList={incomeList} allSubCats={allSubCats} getGroup={getGroup} expenseMap={expenseMap}
              mappings={mappings.mappings} updateMappings={mappings.updateMappings}
              theme={t} isDark={isDark} currency={currency}
              resolvedUserName={resolvedUserName} defaultPayment={settings.defaultPayment}
              onClose={() => setImportOpen(false)}
            />
          </Suspense>
        </ErrorBoundary>
      )}

      <ToastContainer toasts={toasts} onDismiss={removeToast} theme={t} />
    </div>
  );
}
